// ─── src/services/nameSearchService.js ───────────────────────────────────────
// Direct name/brand search against the listings table.
// Bypasses the AI intent pipeline entirely, used when the user types a venue
// or vendor name rather than a style/capacity/location query.
//
// Returns up to 5 matches with the same field shape as the listing rows in
// RegionCategoryPage so they can be rendered by LuxuryVenueCard directly.
// ─────────────────────────────────────────────────────────────────────────────
import { supabase } from "../lib/supabaseClient";

const SELECT = [
  "id, slug, venue_name, category, category_slug, listing_type",
  "country, country_slug, region, region_slug, city",
  "short_description, hero_image, hero_images, media_items, is_featured, status",
  "price_from, price_range, price_currency",
  "lat, lng, styles, capacity_min, content_quality_score",
].join(", ");

// Build an ordered image URL array matching the priority used in LuxuryVenueCard:
// media_items (featured first) → hero_images → hero_image fallback.
function buildImgs(r) {
  if (Array.isArray(r.media_items)) {
    const imgs = r.media_items
      .filter(m => m?.type === 'image' && typeof m?.url === 'string' && m.url.length > 1 && !m.url.endsWith('/'))
      .sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0))
      .map(m => m.url);
    if (imgs.length > 0) return imgs;
  }
  if (Array.isArray(r.hero_images)) {
    const imgs = r.hero_images
      .filter(h => typeof h?.url === 'string' && h.url.length > 1 && !h.url.endsWith('/'))
      .map(h => h.url);
    if (imgs.length > 0) return imgs;
  }
  return r.hero_image ? [r.hero_image] : [];
}

function mapRow(r) {
  return {
    id:               r.id,
    slug:             r.slug,
    name:             r.venue_name || "",
    shortDescription: r.short_description || "",
    heroImage:        r.hero_image  || "",
    imgs:             buildImgs(r),
    region:           r.region      || "",
    regionSlug:       r.region_slug || "",
    countrySlug:      r.country_slug || "",
    city:             r.city        || "",
    category:         r.category_slug || r.category || "",
    categorySlug:     r.category_slug || "",
    listingType:      r.listing_type  || "",
    featured:         r.is_featured ?? false,
    status:           r.status,
    priceFrom:        r.price_from    || "",
    priceRange:       r.price_range   || "",
    priceCurrency:    r.price_currency || "",
    lat:              r.lat ?? null,
    lng:              r.lng ?? null,
    styles:           Array.isArray(r.styles) ? r.styles : [],
    capacity:         r.capacity_min ?? null,
    contentScore:     r.content_quality_score ?? 0,
    _nameMatch:       true,
  };
}

// Strips conversational prefixes so deep-dive queries resolve to the venue name.
// "i want to know more about Villa Nascosta" → "Villa Nascosta"
const DEEP_DIVE_PREFIXES = [
  "i want to know more about ", "i want to know about ",
  "tell me more about ", "tell me about ",
  "what can you tell me about ", "more about ",
  "more details on ", "deep dive on ",
  "give me more details about ", "give me details on ",
  "describe ", "what is the ", "what is ",
  "show me more about ", "info on ", "details on ",
  "information about ", "i'd like to know about ",
  "i'm interested in ", "interested in ",
  "find ", "look up ",
];

/**
 * Extracts the likely venue/brand name from a conversational query.
 * Returns the original query if no prefix is detected.
 */
export function extractVenueName(query) {
  const q = query.toLowerCase().trim();
  for (const prefix of DEEP_DIVE_PREFIXES) {
    if (q.startsWith(prefix)) {
      let name = query.substring(prefix.length).trim();
      // Strip leading articles
      if (/^the /i.test(name)) name = name.substring(4).trim();
      if (/^a /i.test(name)) name = name.substring(2).trim();
      return name;
    }
  }
  return query;
}

// Words to skip in word-by-word fallback, too common to be useful as name tokens
const NAME_NOISE = new Set([
  'hotel', 'house', 'and', 'for', 'with', 'near', 'the', 'inn',
  'lodge', 'arms', 'hall', 'park', 'place', 'london', 'paris',
  'rome', 'milan', 'villa', 'venue', 'wedding',
]);

async function runIlike(namePattern, limit) {
  try {
    const { data, error } = await supabase
      .from("listings")
      .select(SELECT)
      .ilike("venue_name", `%${namePattern}%`)
      .eq("status", "published")
      .limit(limit);
    if (error || !data?.length) return [];
    return data.map(mapRow);
  } catch {
    return [];
  }
}

/**
 * Three-layer fuzzy name search, mirrors the orchestrator's retrieval.ts approach.
 *
 * Layer 1: full phrase substring , "the ritz hotel london" → %the ritz hotel london%
 * Layer 2: word-by-word fallback , tries each word independently (longest first, min 4 chars)
 *                                    "ritz" → matches "The Ritz London"
 * Layer 3: prefix truncation     , drops last 2 chars of longest word to absorb typos
 *                                    "nascost" → matches "Villa Nascosta"
 *
 * @param {string} query  Raw user query, conversational prefixes are stripped automatically.
 * @param {object} [opts]
 * @param {number} [opts.limit=5]
 * @returns {Promise<object[]>}
 */
export async function searchByName(query, { limit = 5 } = {}) {
  const clean = extractVenueName(query.trim());
  if (clean.length < 2) return [];

  // Layer 1: full phrase
  const layer1 = await runIlike(clean, limit);
  if (layer1.length > 0) return layer1;

  // Layer 2: each word independently, longest first, skip noise words
  const words = clean
    .split(/\s+/)
    .filter(w => w.length >= 4 && !NAME_NOISE.has(w.toLowerCase()))
    .sort((a, b) => b.length - a.length);

  for (const word of words) {
    const result = await runIlike(word, limit);
    if (result.length > 0) return result;
  }

  // Layer 3: prefix of longest distinctive word (catches trailing-char typos)
  const longest = words[0];
  if (longest && longest.length >= 5) {
    const prefix = longest.slice(0, longest.length - 2);
    const result = await runIlike(prefix, limit);
    if (result.length > 0) return result;
  }

  return [];
}

// ── Location extraction from a name query ──────────────────────────────────────
// Scans the extracted venue name for known city/region tokens.
// "ritz hotel london" → { city: "London", regionSlug: "london" }
// Used to surface alternative venues when the named venue isn't in the directory.

const CITY_TO_REGION = {
  // England
  'london': { city: 'London', regionSlug: 'london', countrySlug: 'england' },
  'cotswolds': { city: 'Cotswolds', regionSlug: 'cotswolds', countrySlug: 'england' },
  'yorkshire': { city: 'Yorkshire', regionSlug: 'yorkshire', countrySlug: 'england' },
  'cornwall': { city: 'Cornwall', regionSlug: 'cornwall', countrySlug: 'england' },
  'hampshire': { city: 'Hampshire', regionSlug: 'hampshire', countrySlug: 'england' },
  'surrey': { city: 'Surrey', regionSlug: 'surrey', countrySlug: 'england' },
  'kent': { city: 'Kent', regionSlug: 'kent', countrySlug: 'england' },
  'devon': { city: 'Devon', regionSlug: 'devon', countrySlug: 'england' },
  // Scotland
  'edinburgh': { city: 'Edinburgh', regionSlug: 'scotland', countrySlug: 'scotland' },
  'highlands': { city: 'Scottish Highlands', regionSlug: 'highlands', countrySlug: 'scotland' },
  // Italy
  'tuscany': { city: 'Tuscany', regionSlug: 'tuscany', countrySlug: 'italy' },
  'florence': { city: 'Florence', regionSlug: 'tuscany', countrySlug: 'italy' },
  'rome': { city: 'Rome', regionSlug: 'rome', countrySlug: 'italy' },
  'milan': { city: 'Milan', regionSlug: 'lombardy', countrySlug: 'italy' },
  'venice': { city: 'Venice', regionSlug: 'venice', countrySlug: 'italy' },
  'amalfi': { city: 'Amalfi Coast', regionSlug: 'amalfi-coast', countrySlug: 'italy' },
  'positano': { city: 'Positano', regionSlug: 'amalfi-coast', countrySlug: 'italy' },
  'puglia': { city: 'Puglia', regionSlug: 'puglia', countrySlug: 'italy' },
  'sicily': { city: 'Sicily', regionSlug: 'sicily', countrySlug: 'italy' },
  'como': { city: 'Lake Como', regionSlug: 'lake-como', countrySlug: 'italy' },
  // France
  'paris': { city: 'Paris', regionSlug: 'paris', countrySlug: 'france' },
  'provence': { city: 'Provence', regionSlug: 'provence', countrySlug: 'france' },
  'bordeaux': { city: 'Bordeaux', regionSlug: 'bordeaux', countrySlug: 'france' },
  'riviera': { city: 'French Riviera', regionSlug: 'french-riviera', countrySlug: 'france' },
  'cannes': { city: 'Cannes', regionSlug: 'french-riviera', countrySlug: 'france' },
  'nice': { city: 'Nice', regionSlug: 'french-riviera', countrySlug: 'france' },
  // Spain
  'ibiza': { city: 'Ibiza', regionSlug: 'ibiza', countrySlug: 'spain' },
  'mallorca': { city: 'Mallorca', regionSlug: 'mallorca', countrySlug: 'spain' },
  'barcelona': { city: 'Barcelona', regionSlug: 'catalonia', countrySlug: 'spain' },
  'marbella': { city: 'Marbella', regionSlug: 'andalusia', countrySlug: 'spain' },
  // Greece
  'santorini': { city: 'Santorini', regionSlug: 'santorini', countrySlug: 'greece' },
  'mykonos': { city: 'Mykonos', regionSlug: 'mykonos', countrySlug: 'greece' },
  'athens': { city: 'Athens', regionSlug: 'attica', countrySlug: 'greece' },
  // Other
  'bali': { city: 'Bali', regionSlug: 'bali', countrySlug: 'indonesia' },
  'dubai': { city: 'Dubai', regionSlug: 'dubai', countrySlug: 'uae' },
  'marrakech': { city: 'Marrakech', regionSlug: 'marrakech', countrySlug: 'morocco' },
  'lisbon': { city: 'Lisbon', regionSlug: 'lisbon', countrySlug: 'portugal' },
  'vienna': { city: 'Vienna', regionSlug: 'vienna', countrySlug: 'austria' },
  'budapest': { city: 'Budapest', regionSlug: 'budapest', countrySlug: 'hungary' },
};

/**
 * Extracts a known location from within a venue name query.
 * Returns { city, regionSlug, countrySlug } or null if no location found.
 */
export function extractLocationFromName(nameQuery) {
  const words = nameQuery.toLowerCase().split(/\s+/);
  // Try two-word combinations first, then single words
  for (let len = 2; len >= 1; len--) {
    for (let i = 0; i <= words.length - len; i++) {
      const phrase = words.slice(i, i + len).join(' ');
      if (CITY_TO_REGION[phrase]) return CITY_TO_REGION[phrase];
    }
  }
  return null;
}

/**
 * Fetches top published listings for a given region or country.
 * Used as the fallback grid when a searched venue isn't in the directory.
 * Ordered by featured status then content quality score.
 *
 * @param {{ regionSlug?: string, countrySlug?: string, limit?: number }}
 * @returns {Promise<object[]>}
 */
export async function fetchAlternativesByLocation({ regionSlug, countrySlug, limit = 6 } = {}) {
  if (!regionSlug && !countrySlug) return [];
  try {
    let q = supabase
      .from('listings')
      .select(SELECT)
      .eq('status', 'published')
      .order('is_featured', { ascending: false })
      .order('content_quality_score', { ascending: false })
      .limit(limit);

    if (regionSlug) {
      q = q.eq('region_slug', regionSlug);
    } else {
      q = q.ilike('country', `%${countrySlug}%`);
    }

    const { data, error } = await q;
    if (error || !data?.length) return [];
    return data.map(mapRow);
  } catch {
    return [];
  }
}
